import { call, put, select, takeLatest, all } from "typed-redux-saga/macro";
import { postData } from "../../services/ApiService";
import {
  BestsellersProductAction,
  FETCH_BESTSELLERS_PRODUCTS,
  FETCH_FREQUENT_BOUGHT_TOGETHER_PRODUCTS,
  FETCH_PRODUCTS_DETAILS_FOR_FREQ_PRODUCTS,
  FETCH_TRENDING_PRODUCTS,
  FreqBoughtTogetherAction,
  GET_BESTSELLERS_PROD_LIST,
  GET_FREQ_BOUGHT_TOGETHER_LIST,
  GET_TRENDING_PROD_LIST,
  LOAD_PRODUCT_DETAILS_FOR_FREQ_PRODUCTS,
  loadProductsForFreqProductsAction,
  TrendingProductAction,
  FETCH_PRODUCT_IDS_BY_CODES,
  GET_PRODUCT_IDS_BY_CODES,
  FetchProductIdsByCodesAction,
} from "./RecommendationType";
import { acquireEndpoint } from "../../utils/SmartMerchandiserAPI";
import { callApi } from "../../utils/SagaUtils";
import { getData } from "../../services/ApiService";
import { processProductList } from "../../utils/RecommendationsUtils";
import {
  selectCurrentLocale,
  selectCurrentStoreId,
} from "store/store-list/StoreListSelectors";
import { selectCurrentCatalogId } from "store/catalog/CatalogSelectors";
import { FETCH_PRODUCTS_COLORS } from "store/product-list/ProductListTypes";
import { selectStoreListIds } from "store/store-list/StoreListSelectors";
import { FETCH_PRODUCT_IDS_BY_ANALYTIC_IDS } from "store/performance-report/PerformanceReportTypes";
import {
  updateProductDetailsFetched,
  updateStateProductData,
} from "./RecommendationAction";

function* fetchFreqBoughtTogetherList(action: FreqBoughtTogetherAction) {
  try {
    yield* put({ type: FETCH_FREQUENT_BOUGHT_TOGETHER_PRODUCTS.REQUEST });
    const actionType = FETCH_FREQUENT_BOUGHT_TOGETHER_PRODUCTS;
    const constName = Object.keys({
      FETCH_FREQUENT_BOUGHT_TOGETHER_PRODUCTS,
    })[0].toString();
    const { productId, limit, period, isProductIdAnalyticIdAPIAvailable } =
      action.payload;
    const storeId = yield* select(selectCurrentStoreId);
    const catalogId = yield* select(selectCurrentCatalogId);
    const localeCode = yield* select(selectCurrentLocale);
    const headersObj = {
      "x-store-id": storeId,
    };
    const endpoint = acquireEndpoint(constName, productId, period, limit);

    const response = yield* call(
      callApi,
      actionType,
      getData,
      action.payload,
      endpoint,
      headersObj,
    );

    if (response.type === actionType.SUCCESS && response.payload) {
      const { productCodes } = processProductList(response.payload);
      if (productCodes && productCodes.length > 0) {
        if (!isProductIdAnalyticIdAPIAvailable) {
          yield* put({
            type: GET_PRODUCT_IDS_BY_CODES,
            payload: {
              productCodes,
              catalogId,
              storeId,
              localeCode,
            },
          });
        } else {
          yield* fetchProductIdsAndLoadProductList(
            productCodes,
            storeId,
            "freqBoughtTogetherProductIds",
          );
        }
      }
    }
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: FETCH_FREQUENT_BOUGHT_TOGETHER_PRODUCTS.FAILURE,
      message: e.message,
    });
  }
}

function* loadProductListForFeqProductsModal(
  action: loadProductsForFreqProductsAction,
) {
  try {
    const { productIds, isRefreshingAllProducts, isCatalogIdRequired = true } = action.payload;
    const localeCode = yield* select(selectCurrentLocale);
    const storeId = yield* select(selectCurrentStoreId);
    const catalogId = yield* select(selectCurrentCatalogId);
    const storeListIds = yield* select(selectStoreListIds);
    const actionType = FETCH_PRODUCTS_DETAILS_FOR_FREQ_PRODUCTS;
    const constName = Object.keys({
      FETCH_PRODUCTS_DETAILS_FOR_FREQ_PRODUCTS,
    })[0].toString();

    let commaSeperatedProductIds: string = "";

    if (productIds && Array.isArray(productIds) && productIds.length) {
      commaSeperatedProductIds = String(
        productIds.length > 1
          ? productIds.join(",").toString()
          : productIds.length && productIds[0],
      );
    }

    const endpoint = acquireEndpoint(
      constName,
      commaSeperatedProductIds,
      isRefreshingAllProducts ? "true" : "false",
    );
    const headersObj = {
      "x-locale-code": localeCode || "default",
      "x-currency-code": "USD",
      "x-store-id": storeId,
      ...(isCatalogIdRequired && { "x-catalog-id": catalogId }),
    };

    yield* call(
      callApi,
      actionType,
      getData,
      action.payload,
      endpoint,
      headersObj,
    );

    if (productIds && productIds.length > 0) {
      yield* put({
        type: FETCH_PRODUCTS_COLORS.REQUEST,
        payload: {
          productIds,
          storeId,
          localeCode,
          catalogId,
          categoryId: "",
          storeListIds,
          isRefreshingAllProducts: false,
        },
      });
    }
  } catch (e: any) {
    yield* put({
      type: FETCH_PRODUCTS_DETAILS_FOR_FREQ_PRODUCTS.FAILURE,
      message: e.message,
    });
  }
}

function* fetchProductIdsAndLoadProductList(
  analyticsIds: string[],
  storeId: string | null,
  recommendationType: string,
  isCatalogIdRequired: boolean = true,
) {
  try {
    const localeCode = yield* select(selectCurrentLocale);
    const catalogId = yield* select(selectCurrentCatalogId);
    const constName = Object.keys({
      FETCH_PRODUCT_IDS_BY_ANALYTIC_IDS,
    })[0].toString();
    const payload = {
      analyticsIds,
    };
    const headers = {
      "x-locale-code": localeCode || "default",
      "x-currency-code": "USD",
      "x-store-id": storeId,
      "x-catalog-id": catalogId,
    };
    const endpoint = acquireEndpoint(constName);
    const result = yield* call(
      callApi,
      FETCH_PRODUCT_IDS_BY_ANALYTIC_IDS,
      postData,
      payload,
      endpoint,
      headers,
    );
    if (
      result?.type === FETCH_PRODUCT_IDS_BY_ANALYTIC_IDS.SUCCESS &&
      result?.payload?.products?.length > 0
    ) {
      const productIds = result.payload.products
        .map((product: any) => product.productId)
        .filter((id: string) => id != null);
      const products = analyticsIds.map((analyticsId: string) => {
        const product = result.payload.products.find(
          (product: any) => product.analyticsId === analyticsId,
        );
        return {
          productCode: (product ? product.productId : analyticsId) as string,
        };
      });

      if (productIds.length > 0) {
        yield* put(
          updateStateProductData({
            products,
            recommendationType,
          }),
        );
        yield* put({
          type: LOAD_PRODUCT_DETAILS_FOR_FREQ_PRODUCTS,
          payload: {
            productIds,
            catalogId,
            storeId,
            localeCode,
            isRefreshingAllProducts: false,
            isCatalogIdRequired,
          },
        });
      }
    } else {
      yield* put(updateProductDetailsFetched());
    }
  } catch (e: any) {
    console.error("Error fetching product IDs and loading product list:", e);
  }
}

function* fetchBestSellersList(action: BestsellersProductAction) {
  try {
    yield* put({ type: FETCH_BESTSELLERS_PRODUCTS.REQUEST });
    const actionType = FETCH_BESTSELLERS_PRODUCTS;
    const constName = Object.keys({
      FETCH_BESTSELLERS_PRODUCTS,
    })[0].toString();
    const { limit, period, isProductIdAnalyticIdAPIAvailable } = action.payload;
    const storeId = yield* select(selectCurrentStoreId);
    const headersObj = {
      "x-store-id": storeId,
    };
    const endpoint = acquireEndpoint(constName, period, limit);
    const response = yield* call(
      callApi,
      actionType,
      getData,
      action.payload,
      endpoint,
      headersObj,
    );
    if (
      response.type === actionType.SUCCESS &&
      response.payload &&
      isProductIdAnalyticIdAPIAvailable
    ) {
      const { productCodes } = processProductList(response.payload);

      // Fetch detailed product information for the KPI products
      if (productCodes.length > 0) {
        yield* fetchProductIdsAndLoadProductList(
          productCodes,
          storeId,
          "bestsellersProductIds",
          false,
        );
      }
    }
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: FETCH_FREQUENT_BOUGHT_TOGETHER_PRODUCTS.FAILURE,
      message: e.message,
    });
  }
}

function* fetchTrendingProductsList(action: TrendingProductAction) {
  try {
    yield* put({ type: FETCH_TRENDING_PRODUCTS.REQUEST });
    const actionType = FETCH_TRENDING_PRODUCTS;
    const constName = Object.keys({
      FETCH_TRENDING_PRODUCTS,
    })[0].toString();
    const { limit, period, isProductIdAnalyticIdAPIAvailable } = action.payload;
    const storeId = yield* select(selectCurrentStoreId);
    const headersObj = {
      "x-store-id": storeId,
    };
    const endpoint = acquireEndpoint(constName, period, limit);
    const response = yield* call(
      callApi,
      actionType,
      getData,
      action.payload,
      endpoint,
      headersObj,
    );
    if (
      response.type === actionType.SUCCESS &&
      response.payload &&
      isProductIdAnalyticIdAPIAvailable
    ) {
      const { productCodes } = processProductList(response.payload);

      // Fetch detailed product information for the KPI products
      if (productCodes.length > 0) {
        yield* fetchProductIdsAndLoadProductList(
          productCodes,
          storeId,
          "trendingProductIds",
          false,
        );
      }
    }
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: FETCH_TRENDING_PRODUCTS.FAILURE,
      message: e.message,
    });
  }
}

function* fetchProductIdsByCodes(action: FetchProductIdsByCodesAction) {
  try {
    yield* put({ type: FETCH_PRODUCT_IDS_BY_CODES.REQUEST });
    const actionType = FETCH_PRODUCT_IDS_BY_CODES;
    const constName = Object.keys({
      FETCH_PRODUCT_IDS_BY_CODES,
    })[0].toString();

    const { productCodes, catalogId, storeId, localeCode } = action.payload;
    let invalidProductCodes: string[] = [];
    const productIdCodes: { productId: string | null; code: string }[] = [];
    let successResponseCount = 0;

    const headersObj = {
      "x-locale-code": localeCode || "default",
      "x-currency-code": "USD",
      "x-store-id": storeId,
      "x-catalog-id": catalogId,
    };

    const optionsObj = {
      showGenericError: false,
    };

    const fetchAllProductIds = productCodes.map((code) => {
      const endpoint = acquireEndpoint(constName, code);
      return call(
        callApi,
        actionType,
        getData,
        action.payload,
        endpoint,
        headersObj,
        optionsObj,
      );
    });

    const results = yield* all(fetchAllProductIds);

    results.forEach((response, index) => {
      const code = productCodes[index];

      if (response.type === actionType.SUCCESS) {
        successResponseCount++;
      } else if (response.type === actionType.FAILURE) {
        invalidProductCodes.push(code);
      }

      productIdCodes.push({
        productId: response.payload?.productId || null,
        code: code,
      });
    });

    yield* put({
      type: FETCH_PRODUCT_IDS_BY_CODES.SUCCESS,
      payload: {
        invalidProductCodes,
        productIdCodes,
        successCount: successResponseCount,
      },
    });

    const validProductIds = productIdCodes
      .filter((e) => e.productId)
      .map((e) => e.productId);

    if (validProductIds.length > 0) {
      yield* put({
        type: LOAD_PRODUCT_DETAILS_FOR_FREQ_PRODUCTS,
        payload: {
          productIds: validProductIds,
          catalogId,
          storeId,
          localeCode,
          isRefreshingAllProducts: false,
        },
      });
      yield* put({
        type: FETCH_PRODUCTS_COLORS.REQUEST,
        payload: {
          productIds: validProductIds,
          catalogId,
          storeId,
          localeCode,
          isRefreshingAllProducts: false,
        },
      });
    } else {
      yield* put({
        type: FETCH_PRODUCTS_DETAILS_FOR_FREQ_PRODUCTS.SUCCESS,
        payload: {
          results: [],
        },
      });
    }
  } catch (error: any) {
    console.error(error);
    yield* put({
      type: FETCH_PRODUCT_IDS_BY_CODES.FAILURE,
      message: error.message,
    });
  }
}

export function* watchBestsellersProductList() {
  yield* takeLatest(GET_BESTSELLERS_PROD_LIST, fetchBestSellersList);
}

export function* watchTrendingProductList() {
  yield* takeLatest(GET_TRENDING_PROD_LIST, fetchTrendingProductsList);
}

export function* watchfetchFreqBoughtTogetherList() {
  yield* takeLatest(GET_FREQ_BOUGHT_TOGETHER_LIST, fetchFreqBoughtTogetherList);
}

export function* watchfetchProductDetailsForRecommendation() {
  yield* takeLatest(
    LOAD_PRODUCT_DETAILS_FOR_FREQ_PRODUCTS,
    loadProductListForFeqProductsModal,
  );
}

export function* watchFetchProductIdsByCodes() {
  yield* takeLatest(GET_PRODUCT_IDS_BY_CODES, fetchProductIdsByCodes);
}
